import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

import { ChartTooltipBox } from './ChartTooltip';
import {
  BAR_MAX_SIZE,
  CHART_INK,
  CHART_SURFACE,
  WEATHER_COLORS,
  Y_AXIS_WIDTH,
} from './chartTheme';
import { COMMON_TRANSLATE_KEYS } from '../../constants/translate-keys';

export type WeatherPoint = {
  label: string;
  title: string;
  temperature: number | null;
  rain: number | null;
};

interface WeatherStripsProps {
  data: WeatherPoint[];
  /** Shared with the chart above so hovering highlights the same day. */
  syncId: string;
}

export const formatTemperature = (value: number) => `${value.toFixed(1)}°C`;
export const formatRain = (value: number) => `${value.toFixed(1)} mm`;

const MARGIN = { top: 4, right: 8, left: 0, bottom: 0 };

/**
 * Average temperature and rain under a per-day chart. Each measure gets
 * its own small chart with its own scale, aligned to the same days.
 */
const WeatherStrips = ({ data, syncId }: WeatherStripsProps) => {
  const { t } = useTranslation();

  const renderTooltip =
    (key: 'temperature' | 'rain') =>
    ({ active, payload }: any) => {
      if (!active || !payload?.length) {
        return null;
      }

      const point: WeatherPoint = payload[0].payload;
      const value = point[key];

      return (
        <ChartTooltipBox
          title={point.title}
          rows={[
            {
              key,
              label:
                key === 'temperature'
                  ? t(COMMON_TRANSLATE_KEYS.STATS_TEMPERATURE)
                  : t(COMMON_TRANSLATE_KEYS.STATS_RAIN),
              color: WEATHER_COLORS[key],
              value:
                value === null
                  ? '–'
                  : key === 'temperature'
                  ? formatTemperature(value)
                  : formatRain(value),
            },
          ]}
        />
      );
    };

  return (
    <div className="flex flex-col gap-2">
      <p className="flex items-center gap-2 text-xs text-textGray">
        <span
          className="inline-block w-3 h-0.5 rounded"
          style={{ backgroundColor: WEATHER_COLORS.temperature }}
        />
        {t(COMMON_TRANSLATE_KEYS.STATS_TEMPERATURE)} (°C)
      </p>
      <div className="h-[90px] w-full">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={data} margin={MARGIN} syncId={syncId}>
            <CartesianGrid vertical={false} stroke={CHART_INK.grid} />
            <XAxis dataKey="label" hide />
            <YAxis
              width={Y_AXIS_WIDTH}
              tickCount={3}
              tick={{ fill: CHART_INK.muted, fontSize: 11 }}
              tickFormatter={(value: number) => `${Math.round(value)}°`}
              axisLine={false}
              tickLine={false}
              domain={['auto', 'auto']}
            />
            <Tooltip
              cursor={{ stroke: CHART_INK.axis }}
              content={renderTooltip('temperature')}
            />
            <Line
              dataKey="temperature"
              stroke={WEATHER_COLORS.temperature}
              strokeWidth={2}
              strokeLinecap="round"
              strokeLinejoin="round"
              dot={{
                r: 3,
                fill: WEATHER_COLORS.temperature,
                stroke: CHART_SURFACE,
                strokeWidth: 2,
              }}
              activeDot={{ r: 5, stroke: CHART_SURFACE, strokeWidth: 2 }}
              connectNulls
              isAnimationActive={false}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>

      <p className="flex items-center gap-2 text-xs text-textGray">
        <span
          className="inline-block w-3 h-3 rounded-sm"
          style={{ backgroundColor: WEATHER_COLORS.rain }}
        />
        {t(COMMON_TRANSLATE_KEYS.STATS_RAIN)} (mm)
      </p>
      <div className="h-[70px] w-full">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={data} margin={MARGIN} syncId={syncId}>
            <CartesianGrid vertical={false} stroke={CHART_INK.grid} />
            <XAxis dataKey="label" hide />
            <YAxis
              width={Y_AXIS_WIDTH}
              tickCount={3}
              allowDecimals={false}
              tick={{ fill: CHART_INK.muted, fontSize: 11 }}
              axisLine={false}
              tickLine={false}
            />
            <Tooltip
              cursor={{ fill: 'rgba(255,255,255,0.05)' }}
              content={renderTooltip('rain')}
            />
            <Bar
              dataKey="rain"
              fill={WEATHER_COLORS.rain}
              maxBarSize={BAR_MAX_SIZE}
              radius={[4, 4, 0, 0]}
              isAnimationActive={false}
            />
          </BarChart>
        </ResponsiveContainer>
      </div>
      <p className="text-xxs text-textGray text-right">
        {t(COMMON_TRANSLATE_KEYS.STATS_WEATHER_SOURCE)}
      </p>
    </div>
  );
};

export default WeatherStrips;
